import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { reportMutationError } from "./errors";
import { viewerEpoch } from "./viewer-epoch";

export interface OptimisticUpdate<TData = unknown> {
  queryKey: QueryKey;
  updater: (previous: TData | undefined) => TData;
  /**
   * Applies the update to every cached query *under* `queryKey` rather than to
   * that exact key — for a parameterised list such as
   * `["stores", "list", { sort, page }]`, which has one entry per set of params
   * and cannot be enumerated from a mutation hook.
   *
   * Off by default, and deliberately so: key hierarchies nest (a product's
   * related-products list lives under its detail key), so a prefix update would
   * otherwise hand an updater written for one shape a neighbour of a different
   * one. Only set it where the updater tolerates every entry it can reach.
   */
  matchPrefix?: boolean;
}

export interface OptimisticMutationConfig<TVariables> {
  queryClient: QueryClient;
  /** Every query key touched by this mutation — cancelled, snapshotted and invalidated together. */
  affectedKeys: (variables: TVariables) => QueryKey[];
  /**
   * Keys touched by this mutation that are matched exactly: cancelled, snapshotted and invalidated without
   * reaching the queries nested under them (a product detail's reviews and related lists, for instance).
   */
  exactAffectedKeys?: (variables: TVariables) => QueryKey[];
  /**
   * Applies the optimistic change to each affected key's current cache
   * value. Each entry's `TData` is naturally different per key (a `Cart`
   * here, a `Creator` there), so this list is intentionally typed with
   * `OptimisticUpdate<any>` — the alternative is losing type inference on
   * every call site's own `updater` in exchange for a homogeneous array
   * type this helper never actually needs to be homogeneous.
   *
   * `exactKeys` is what `exactAffectedKeys` returned for this mutation, resolved once in `onMutate`: a key set
   * that depends on the cache (which products embed a store) must be the same set that is cancelled,
   * snapshotted, written and invalidated, or a query cached in between is written but never rolled back.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apply: (variables: TVariables, resolved: { exactKeys: QueryKey[] }) => OptimisticUpdate<any>[];
  /** Label surfaced to the injected mutation-error handler (see queries/errors.ts). */
  action: string;
}

export interface OptimisticContext {
  snapshots: [QueryKey, unknown][];
  /** What each snapshotted key held right after the optimistic write, index-aligned with `snapshots`. */
  written: unknown[];
  /** The exact keys resolved in `onMutate`, invalidated again in `onSettled`. */
  exactKeys: QueryKey[];
  /** The viewer the cache belonged to when the optimistic write was made (see viewer-epoch.ts). */
  viewerEpoch: number;
}

/**
 * The one optimistic-mutation pattern used by every mutation hook (plan §5):
 * `onMutate` cancels in-flight fetches for the affected keys, snapshots their
 * current values, then applies the optimistic update to each. `onError`
 * rolls back every key the mutation's write still owns and reports the
 * failure. `onSettled` invalidates every affected key so the server's real
 * state eventually wins.
 *
 * Spread the result into `useMutation({ mutationFn, ...createOptimisticHandlers(...) })`.
 */
export function createOptimisticHandlers<TVariables>(config: OptimisticMutationConfig<TVariables>): {
  onMutate: (variables: TVariables) => Promise<OptimisticContext>;
  onError: (error: unknown, variables: TVariables, context: OptimisticContext | undefined) => void;
  onSettled: (data: unknown, error: unknown, variables: TVariables, context?: OptimisticContext) => void;
} {
  const { queryClient, affectedKeys, exactAffectedKeys, apply, action } = config;

  return {
    onMutate: async (variables) => {
      const keys = affectedKeys(variables);
      const exactKeys = exactAffectedKeys?.(variables) ?? [];
      await Promise.all([
        ...keys.map((key) => queryClient.cancelQueries({ queryKey: key })),
        ...exactKeys.map((key) => queryClient.cancelQueries({ queryKey: key, exact: true })),
      ]);

      const updates = apply(variables, { exactKeys });
      // A prefix update can touch several cached variants, so every one of them is
      // snapshotted; an exact update snapshots only its own key, which may not exist yet.
      const prefixes = updates.filter((update) => update.matchPrefix).map((update) => update.queryKey);
      const snapshots: [QueryKey, unknown][] = [
        ...[...keys, ...exactKeys].map((key): [QueryKey, unknown] => [key, queryClient.getQueryData(key)]),
        ...prefixes.flatMap((key) => queryClient.getQueriesData({ queryKey: key })),
      ];

      for (const update of updates) {
        if (update.matchPrefix) {
          queryClient.setQueriesData({ queryKey: update.queryKey }, (previous: unknown) =>
            update.updater(previous),
          );
        } else {
          queryClient.setQueryData(update.queryKey, (previous: unknown) => update.updater(previous));
        }
      }

      // Recorded after the write, so onError can tell whether the cache still holds what this mutation put there.
      const written = snapshots.map(([key]) => queryClient.getQueryData(key));
      return { snapshots, written, exactKeys, viewerEpoch: viewerEpoch(queryClient) };
    },

    onError: (error, _variables, context) => {
      // The viewer changed while this was in flight: every snapshot is the previous viewer's state. Comparing data
      // can't catch this, since structural sharing may keep the very same reference after the session clear.
      const owned = context && context.viewerEpoch === viewerEpoch(queryClient) ? context : undefined;
      owned?.snapshots.forEach(([key, value], index) => {
        const query = queryClient.getQueryCache().find({ queryKey: key, exact: true });
        // Only roll back what this mutation's write still owns. A key removed (sign-out), reset (session
        // changes reset viewer-relative queries) or refetched while the mutation was in flight holds data
        // that isn't this write's: restoring the snapshot would resurrect the previous viewer's state.
        if (!query || !Object.is(query.state.data, owned.written[index])) return;
        queryClient.setQueryData(key, value);
      });
      reportMutationError(error, { action });
    },

    onSettled: (_data, _error, variables, context) => {
      for (const key of affectedKeys(variables)) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      // The same set onMutate cancelled and wrote; recomputing here could reach a query this mutation never touched.
      for (const key of context?.exactKeys ?? exactAffectedKeys?.(variables) ?? []) {
        void queryClient.invalidateQueries({ queryKey: key, exact: true });
      }
    },
  };
}
